export const ADMIN_GROUPS = {
  superAdmin: 'super-admin',
  contentAdmin: 'content-admin',
} as const;

export type AdminRole = (typeof ADMIN_GROUPS)[keyof typeof ADMIN_GROUPS];

export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  [ADMIN_GROUPS.superAdmin]: 'Super Admin',
  [ADMIN_GROUPS.contentAdmin]: 'Content Admin',
};

export const ALL_ADMIN_ROLES: AdminRole[] = [
  ADMIN_GROUPS.superAdmin,
  ADMIN_GROUPS.contentAdmin,
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidAdminEmail = (email: string): boolean =>
  EMAIL_PATTERN.test(email);

/**
 * Parse Cognito groups from JWT claims.
 * Handles:
 * - real arrays (raw JWT / frontend)
 * - HTTP API JWT authorizer format: "[group1 group2]"
 * - REST Cognito authorizer format: "group1,group2"
 * - JSON array strings: '["group1","group2"]'
 * - single group strings: "super-admin"
 */
export const parseAdminGroups = (
  groups: string[] | string | undefined | null,
): AdminRole[] => {
  let raw: string[] = [];

  if (Array.isArray(groups)) {
    raw = groups.map(String);
  } else if (typeof groups === 'string') {
    const trimmed = groups.trim();
    if (!trimmed) {
      raw = [];
    } else if (trimmed.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          raw = parsed.map(String);
        }
      } catch {
        // HTTP API style: "[group1 group2]" — not valid JSON
        raw = trimmed
          .replace(/^\[/, '')
          .replace(/\]$/, '')
          .split(/[,\s]+/)
          .map((group) => group.trim())
          .filter(Boolean);
      }
    } else if (trimmed.includes(',')) {
      raw = trimmed.split(',').map((group) => group.trim());
    } else if (trimmed.includes(' ')) {
      raw = trimmed.split(/\s+/).map((group) => group.trim());
    } else {
      raw = [trimmed];
    }
  }

  return raw.filter((group): group is AdminRole =>
    ALL_ADMIN_ROLES.includes(group as AdminRole),
  );
};

export const resolveAdminRole = (
  groups: string[] | string | undefined | null,
): AdminRole | null => {
  const parsed = parseAdminGroups(groups);
  if (parsed.includes(ADMIN_GROUPS.superAdmin)) {
    return ADMIN_GROUPS.superAdmin;
  }
  if (parsed.includes(ADMIN_GROUPS.contentAdmin)) {
    return ADMIN_GROUPS.contentAdmin;
  }
  return null;
};

export const canManageContent = (role: AdminRole | null): boolean =>
  role === ADMIN_GROUPS.superAdmin || role === ADMIN_GROUPS.contentAdmin;

export const canManageUsers = (role: AdminRole | null): boolean =>
  role === ADMIN_GROUPS.superAdmin;
